import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { ACTIVITY_TYPES, ACTIVITY_TYPE_COLORS, MOCK_TASKS } from '../plannerMockData';
import { WEEKDAY_LABELS, DAILY_TARGET_MINUTES, formatDuration, getDayKey, minutesToTimeLabel } from '../plannerUtils';

const DURATION_OPTIONS = [10, 15, 20, 30, 45, 60, 90, 120, 150, 180, 240, 300, 360, 420, 480, 600, 720];
const DAY_MINUTES = 24 * 60;
const MIN_DAY_MINUTES = DAILY_TARGET_MINUTES; // 8h — mandatory minimum before a day can be saved
const TIME_BASE = '2000-01-01T00:00:00';

const timeToDate = (minutes) => {
  const d = new Date(TIME_BASE);
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
};
const dateToTime = (date) => date.getHours() * 60 + date.getMinutes();

const makeRow = (startMinutes, durationMinutes, overrides = {}) => ({
  id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  startMinutes,
  durationMinutes,
  type: 'Task',
  taskId: '',
  notes: '',
  ...overrides,
});

const rowsOverlap = (a, b) =>
  a.startMinutes < b.startMinutes + b.durationMinutes && b.startMinutes < a.startMinutes + a.durationMinutes;

/** Maps each row id to the other rows it overlaps with, so the row list can flag conflicts live. */
const getOverlapMap = (rows) => {
  const map = new Map();
  for (let i = 0; i < rows.length; i += 1) {
    for (let j = i + 1; j < rows.length; j += 1) {
      if (rowsOverlap(rows[i], rows[j])) {
        map.set(rows[i].id, [...(map.get(rows[i].id) || []), rows[j]]);
        map.set(rows[j].id, [...(map.get(rows[j].id) || []), rows[i]]);
      }
    }
  }
  return map;
};

const activitiesToRows = (activities) =>
  [...activities]
    .sort((a, b) => a.startMinutes - b.startMinutes)
    .map((a) =>
      makeRow(a.startMinutes, a.endMinutes - a.startMinutes, {
        id: a.id,
        type: a.type,
        taskId: a.taskId || '',
        notes: a.notes || '',
      })
    );

const BulkDayDialog = ({ open, onClose, weekDates, initialDay, getDayActivities, onSaveDay }) => {
  const [day, setDay] = useState(initialDay);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    setDay(initialDay);
    setRows(activitiesToRows(getDayActivities(initialDay)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDay]);

  const handleDayChange = (newDay) => {
    setDay(newDay);
    setRows(activitiesToRows(getDayActivities(newDay)));
  };

  const dayIndex = weekDates.findIndex((d) => getDayKey(d) === day);
  const goToAdjacentDay = (offset) => {
    const newIndex = dayIndex + offset;
    if (newIndex < 0 || newIndex >= weekDates.length) return;
    handleDayChange(getDayKey(weekDates[newIndex]));
  };

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const totalBookedMinutes = rows.reduce((sum, r) => sum + r.durationMinutes, 0);
  const remainingMinutes = Math.max(DAY_MINUTES - totalBookedMinutes, 0);
  const dayFull = totalBookedMinutes >= DAY_MINUTES;
  const belowMinimum = totalBookedMinutes < MIN_DAY_MINUTES;
  const minutesUntilMinimum = MIN_DAY_MINUTES - totalBookedMinutes;

  const addRow = () => {
    // Suggest the next genuinely free slot — the latest end time across *all* rows, not just
    // the last one added — so a new row doesn't land inside a block booked earlier.
    const occupiedUntil = rows.reduce((max, r) => Math.max(max, r.startMinutes + r.durationMinutes), 0);
    const suggestedStart = Math.min(occupiedUntil, DAY_MINUTES);
    if (suggestedStart >= DAY_MINUTES) return;
    const suggestedDuration = Math.min(30, DAY_MINUTES - suggestedStart);
    const last = rows[rows.length - 1];
    setRows((prev) => [...prev, makeRow(suggestedStart, suggestedDuration, { type: last ? last.type : 'Task' })]);
  };

  const handleSave = () => {
    const activities = rows.map((r) => ({
      id: r.id.startsWith('row-') ? undefined : r.id,
      day,
      startMinutes: r.startMinutes,
      endMinutes: Math.min(r.startMinutes + r.durationMinutes, DAY_MINUTES),
      type: r.type,
      taskId: r.type === 'Task' ? r.taskId : null,
      notes: r.notes,
    }));
    onSaveDay(day, activities);
  };

  // Sorted-by-start coverage strip: colored segment per activity, light gray for any gap.
  const coverageSegments = [];
  const sortedRows = [...rows].sort((a, b) => a.startMinutes - b.startMinutes);
  let cursor = 0;
  sortedRows.forEach((r) => {
    if (r.startMinutes > cursor) {
      coverageSegments.push({ kind: 'gap', minutes: r.startMinutes - cursor });
    }
    coverageSegments.push({ kind: 'row', minutes: r.durationMinutes, type: r.type });
    cursor = Math.max(cursor, r.startMinutes + r.durationMinutes);
  });
  if (cursor < DAY_MINUTES) coverageSegments.push({ kind: 'gap', minutes: DAY_MINUTES - cursor });

  const overlapMap = getOverlapMap(rows);
  const hasOverlaps = overlapMap.size > 0;
  const missingDetails = !rows.every((r) => r.type !== 'Task' || r.taskId);
  const canSave = !hasOverlaps && !missingDetails && !belowMinimum;

  const saveDisabledReason = hasOverlaps
    ? 'Resolve the overlapping activities highlighted below before saving.'
    : belowMinimum
    ? `Plan at least 8h before saving — ${formatDuration(minutesUntilMinimum)} more needed.`
    : missingDetails
    ? 'Select a task for every row before saving.'
    : '';

  const dayLabel = day ? format(new Date(`${day}T00:00:00`), 'EEEE, dd MMMM yyyy') : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Plan Full Day
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
            {dayLabel}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Previous day">
                <span>
                  <IconButton size="small" onClick={() => goToAdjacentDay(-1)} disabled={dayIndex <= 0} aria-label="Previous day">
                    <ChevronLeftIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="bulk-day-label" shrink>
                  Day
                </InputLabel>
                <Select
                  labelId="bulk-day-label"
                  label="Day"
                  notched
                  value={day}
                  onChange={(e) => handleDayChange(e.target.value)}
                >
                  {weekDates.map((d, i) => (
                    <MenuItem key={getDayKey(d)} value={getDayKey(d)}>
                      {WEEKDAY_LABELS[i]} ({d.getDate()}/{d.getMonth() + 1})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Next day">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => goToAdjacentDay(1)}
                    disabled={dayIndex >= weekDates.length - 1}
                    aria-label="Next day"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography
              variant="body2"
              color={belowMinimum ? 'error.main' : dayFull ? 'success.main' : 'text.secondary'}
              fontWeight={600}
            >
              {formatDuration(totalBookedMinutes)} of 24h booked
              {belowMinimum
                ? ` — 8h minimum required (${formatDuration(minutesUntilMinimum)} more needed)`
                : dayFull
                ? ' — fully booked'
                : ` — ${formatDuration(remainingMinutes)} unbooked`}
            </Typography>
          </Box>

          {belowMinimum && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
              Every day needs at least 8 hours planned before it can be saved. You've planned{' '}
              {formatDuration(totalBookedMinutes)} so far — add {formatDuration(minutesUntilMinimum)} more.
            </Alert>
          )}

          {hasOverlaps && (
            <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2, borderRadius: '10px' }}>
              {overlapMap.size} {overlapMap.size === 1 ? 'activity overlaps' : 'activities overlap'} with another
              activity below — the clashing rows are highlighted in red. Fix the times before you can save this day.
            </Alert>
          )}

          <Box className="planner-bulk-coverage-wrap">
            <Box className="planner-bulk-coverage" role="img" aria-label="24 hour coverage overview">
              {coverageSegments.map((seg, i) =>
                seg.kind === 'row' ? (
                  <Tooltip key={i} title={`${seg.type} · ${formatDuration(seg.minutes)}`}>
                    <Box
                      className="planner-bulk-coverage-segment"
                      sx={{ flexGrow: seg.minutes, backgroundColor: ACTIVITY_TYPE_COLORS[seg.type] || '#546e7a' }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip key={i} title={`Unbooked · ${formatDuration(seg.minutes)}`}>
                    <Box className="planner-bulk-coverage-segment gap" sx={{ flexGrow: seg.minutes }} />
                  </Tooltip>
                )
              )}
            </Box>
            <Tooltip title="8h minimum">
              <Box className="planner-bulk-coverage-marker" style={{ left: `${(MIN_DAY_MINUTES / DAY_MINUTES) * 100}%` }}>
                <span className="planner-bulk-coverage-marker-label">Min 8h</span>
              </Box>
            </Tooltip>
          </Box>

          <Box className="planner-bulk-rows">
            {rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No activities yet for this day — add your first block below.
              </Typography>
            )}
            {rows.map((row) => {
              const conflicts = overlapMap.get(row.id);
              return (
              <Box key={row.id} className={`planner-bulk-row${conflicts ? ' has-overlap' : ''}`}>
              <Box className="planner-bulk-row-fields">
                <TimePicker
                  label="Start"
                  value={timeToDate(row.startMinutes)}
                  onChange={(v) => v && updateRow(row.id, { startMinutes: dateToTime(v) })}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 130 },
                      error: Boolean(conflicts),
                    },
                  }}
                />
                <FormControl size="small" sx={{ width: 110 }}>
                  <InputLabel id={`dur-${row.id}`}>Duration</InputLabel>
                  <Select
                    labelId={`dur-${row.id}`}
                    label="Duration"
                    value={row.durationMinutes}
                    onChange={(e) => updateRow(row.id, { durationMinutes: Number(e.target.value) })}
                  >
                    {DURATION_OPTIONS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {formatDuration(m)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" className="planner-bulk-end" color="text.secondary">
                  → {minutesToTimeLabel(Math.min(row.startMinutes + row.durationMinutes, DAY_MINUTES))}
                </Typography>
                <FormControl size="small" sx={{ width: 150 }}>
                  <InputLabel id={`type-${row.id}`}>Type</InputLabel>
                  <Select
                    labelId={`type-${row.id}`}
                    label="Type"
                    value={row.type}
                    onChange={(e) => updateRow(row.id, { type: e.target.value })}
                  >
                    {ACTIVITY_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {row.type === 'Task' && (
                  <FormControl size="small" sx={{ width: 190 }} error={!row.taskId}>
                    <InputLabel id={`task-${row.id}`}>Task</InputLabel>
                    <Select
                      labelId={`task-${row.id}`}
                      label="Task"
                      value={row.taskId}
                      onChange={(e) => updateRow(row.id, { taskId: e.target.value })}
                    >
                      {MOCK_TASKS.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label="Notes"
                  size="small"
                  value={row.notes}
                  onChange={(e) => updateRow(row.id, { notes: e.target.value })}
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <IconButton aria-label="Remove activity" onClick={() => removeRow(row.id)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
              {conflicts && (
                <Typography variant="caption" className="planner-bulk-overlap-caption">
                  <WarningAmberIcon fontSize="inherit" /> Overlaps with{' '}
                  {conflicts
                    .map((c) => `${c.type} (${minutesToTimeLabel(c.startMinutes)}–${minutesToTimeLabel(c.startMinutes + c.durationMinutes)})`)
                    .join(', ')}
                </Typography>
              )}
              </Box>
              );
            })}
          </Box>

          <Button startIcon={<AddIcon />} onClick={addRow} disabled={dayFull} sx={{ mt: 1 }}>
            Add Activity
          </Button>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Tooltip title={canSave ? '' : saveDisabledReason}>
          <span>
            <Button variant="contained" onClick={handleSave} disabled={!canSave}>
              Save Day
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default BulkDayDialog;
